import React, { useState, useEffect } from 'react';
import { Search, Eye, UserPlus, MapPin, Plus, Trash2, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { usePagination } from '../../hooks/usePagination';
import TablePagination from '../../components/ui/table-pagination';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Separator } from '../../components/ui/separator';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCustomersList = () => {
  const { toast } = useToast();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  
  const [newAddress, setNewAddress] = useState({
    type: 'shipping',
    fullName: '',
    street: '',
    number: '',
    apartment: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    phone: '',
    isDefault: false
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      
      const response = await fetch(`${API_URL}/api/auth/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const mappedCustomers = data.map(user => ({
          id: user.email,
          name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'N/A',
          email: user.email,
          phone: user.phone || 'N/A',
          source: user.source || 'direct',
          role: user.role,
          newsletter: user.newsletter_subscribed || false,
          joinDate: user.createdAt,
          status: user.isActive ? 'active' : 'inactive',
          fullData: user
        }));
        
        // Sort by joinDate: newest first, oldest last
        mappedCustomers.sort((a, b) => {
          const dateA = new Date(a.joinDate || 0);
          const dateB = new Date(b.joinDate || 0);
          return dateB - dateA; // Descending order (newest first)
        });
        
        setCustomers(mappedCustomers);
      } else {
        throw new Error('Failed to fetch customers');
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast({
        title: 'Error',
        description: 'Failed to load customers',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerAddresses = async (email) => {
    setLoadingAddresses(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/addresses/admin/user/${email}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCustomerAddresses(data);
      }
    } catch (error) {
      console.error('Error fetching addresses:', error);
    } finally {
      setLoadingAddresses(false);
    }
  };

  const handleViewDetails = (customer) => {
    setSelectedCustomer(customer);
    fetchCustomerAddresses(customer.email);
  };

  const handleAddAddress = async () => {
    if (!newAddress.fullName || !newAddress.street || !newAddress.city || !newAddress.country) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/addresses/admin/user/${selectedCustomer.email}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newAddress)
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Address added successfully'
        });
        fetchCustomerAddresses(selectedCustomer.email);
        setShowAddressForm(false);
        setNewAddress({
          type: 'shipping',
          fullName: '',
          street: '',
          number: '',
          apartment: '',
          city: '',
          state: '',
          postalCode: '',
          country: '',
          phone: '',
          isDefault: false
        });
      } else {
        throw new Error('Failed to add address');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/addresses/admin/${addressId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Address deleted successfully'
        });
        fetchCustomerAddresses(selectedCustomer.email);
      } else {
        throw new Error('Failed to delete address');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const filteredCustomers = customers.filter((customer) => {
    const matchesSearch =
      customer.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const {
    currentPage,
    totalPages,
    currentItems: paginatedCustomers,
    goToPage,
    totalItems,
    startIndex,
    endIndex,
  } = usePagination(filteredCustomers, 20);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#be1f1a]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
          <p className="text-gray-600 mt-1">{totalItems} customers</p>
        </div>
      </div>

      {/* Search */}
      <div className="flex gap-4">
        <div className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      </div>

      {/* Customers Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Join Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedCustomers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-gray-500">
                  No customers found
                </TableCell>
              </TableRow>
            ) : (
              paginatedCustomers.map((customer) => (
                <TableRow key={customer.id}>
                  <TableCell className="font-medium">{customer.name}</TableCell>
                  <TableCell>{customer.email}</TableCell>
                  <TableCell>{customer.phone}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      customer.role === 'admin' 
                        ? 'bg-purple-100 text-purple-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {customer.role}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">
                    {customer.joinDate ? new Date(customer.joinDate).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    }) : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        customer.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {customer.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleViewDetails(customer)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          startIndex={startIndex}
          endIndex={endIndex}
          onPageChange={goToPage}
        />
      </div>

      {/* Customer Detail Modal */}
      <Dialog open={!!selectedCustomer} onOpenChange={() => setSelectedCustomer(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Customer Details</DialogTitle>
            <DialogDescription>
              View and manage customer information and addresses
            </DialogDescription>
          </DialogHeader>

          {selectedCustomer && (
            <div className="space-y-6">
              {/* Customer Info */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Personal Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Name:</span>
                    <p className="font-medium">{selectedCustomer.name}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Email:</span>
                    <p className="font-medium">{selectedCustomer.email}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Phone:</span>
                    <p className="font-medium">{selectedCustomer.phone}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Role:</span>
                    <p className="font-medium capitalize">{selectedCustomer.role}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Status:</span>
                    <p className="font-medium capitalize">{selectedCustomer.status}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Join Date:</span>
                    <p className="font-medium">
                      {selectedCustomer.joinDate ? new Date(selectedCustomer.joinDate).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Addresses */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-[#be1f1a]" />
                    Addresses ({customerAddresses.length})
                  </h3>
                  <Button
                    size="sm"
                    onClick={() => setShowAddressForm(!showAddressForm)}
                    className="bg-[#be1f1a] hover:bg-[#a71b17]"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Address
                  </Button>
                </div>

                {/* Add Address Form */}
                {showAddressForm && (
                  <div className="bg-gray-50 p-4 rounded-lg mb-4 space-y-3">
                    <h4 className="font-medium text-sm">New Address</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="fullName" className="text-xs">Full Name *</Label>
                        <Input
                          id="fullName"
                          value={newAddress.fullName}
                          onChange={(e) => setNewAddress({...newAddress, fullName: e.target.value})}
                          className="h-9 text-sm"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-xs">Phone</Label>
                        <Input
                          id="phone"
                          value={newAddress.phone}
                          onChange={(e) => setNewAddress({...newAddress, phone: e.target.value})}
                          className="h-9 text-sm"
                        />
                      </div>
                      <div>
                        <Label htmlFor="street" className="text-xs">Street *</Label>
                        <Input
                          id="street"
                          value={newAddress.street}
                          onChange={(e) => setNewAddress({...newAddress, street: e.target.value})}
                          className="h-9 text-sm"
                        />
                      </div>
                      <div>
                        <Label htmlFor="number" className="text-xs">Number</Label>
                        <Input
                          id="number"
                          value={newAddress.number}
                          onChange={(e) => setNewAddress({...newAddress, number: e.target.value})}
                          className="h-9 text-sm"
                        />
                      </div>
                      <div>
                        <Label htmlFor="city" className="text-xs">City *</Label>
                        <Input
                          id="city"
                          value={newAddress.city}
                          onChange={(e) => setNewAddress({...newAddress, city: e.target.value})}
                          className="h-9 text-sm"
                        />
                      </div>
                      <div>
                        <Label htmlFor="state" className="text-xs">State</Label>
                        <Input
                          id="state"
                          value={newAddress.state}
                          onChange={(e) => setNewAddress({...newAddress, state: e.target.value})}
                          className="h-9 text-sm"
                        />
                      </div>
                      <div>
                        <Label htmlFor="postalCode" className="text-xs">Postal Code</Label>
                        <Input
                          id="postalCode"
                          value={newAddress.postalCode}
                          onChange={(e) => setNewAddress({...newAddress, postalCode: e.target.value})}
                          className="h-9 text-sm"
                        />
                      </div>
                      <div>
                        <Label htmlFor="country" className="text-xs">Country *</Label>
                        <Input
                          id="country"
                          value={newAddress.country}
                          onChange={(e) => setNewAddress({...newAddress, country: e.target.value})}
                          className="h-9 text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleAddAddress} className="bg-[#be1f1a] hover:bg-[#a71b17]">
                        Save Address
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setShowAddressForm(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {/* Addresses List */}
                {loadingAddresses ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#be1f1a] mx-auto"></div>
                  </div>
                ) : customerAddresses.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No addresses found</p>
                ) : (
                  <div className="space-y-3">
                    {customerAddresses.map((address) => (
                      <div key={address.id} className="border rounded-lg p-4 hover:border-[#be1f1a] transition-colors">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-medium">{address.fullName}</h4>
                              {address.isDefault && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">
                                  Default
                                </span>
                              )}
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full capitalize">
                                {address.type}
                              </span>
                              {address.source === 'order' && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                                  From Order #{address.orderNumber}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">
                              {address.street} {address.number}
                              {address.apartment && `, ${address.apartment}`}
                            </p>
                            <p className="text-sm text-gray-600">
                              {address.city}, {address.state} {address.postalCode}
                            </p>
                            <p className="text-sm text-gray-600">{address.country}</p>
                            {address.phone && (
                              <p className="text-sm text-gray-600">Phone: {address.phone}</p>
                            )}
                          </div>
                          {address.source !== 'order' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteAddress(address.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCustomersList;
